import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  FaSpinner,
  FaEye,
  FaTrash,
  FaCheck,
  FaTimes,
  FaFilePdf,
  FaCalendarAlt,
  FaEdit,
  FaTimesCircle,
  FaCheckCircle,
  FaClock,
  FaUpload,
  FaPlus,
  FaSearch,
  FaGraduationCap,
} from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";
import { buildApiUrl } from "../../utils/apiConfig";

const MQPManagement = ({ isDarkMode }) => {
  const [mqps, setMqps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingMqp, setEditingMqp] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [schemeFilter, setSchemeFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    examYear: "",
    examType: "Model",
    status: "approved",
    scheme: "",
  });

  const [uploadFormData, setUploadFormData] = useState({
    title: "",
    description: "",
    subject: "",
    subjectCode: "",
    examYear: "",
    examType: "Model",
    scheme: "",
  });

  const examTypes = ["Model", "Internal", "External", "CIE", "SEE"];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) =>
    (currentYear - i).toString(),
  );

  useEffect(() => {
    fetchMQPs();
  }, []);

  const fetchMQPs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        "/api/mqp/admin/all",
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setMqps(response.data);
    } catch (error) {
      console.error("Fetch MQPs error:", error);
      toast.error("Failed to fetch MQPs");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `/api/mqp/${id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`MQP ${status}`);
      fetchMQPs();
    } catch (error) {
      console.error("Update status error:", error);
      toast.error("Failed to update status");
    }
  };

  const deleteMQP = async (id) => {
    if (!window.confirm("Delete this MQP permanently?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/mqp/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("MQP deleted");
      fetchMQPs();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete");
    }
  };

  const updateMQP = async () => {
    if (!formData.examYear || !formData.scheme) {
      toast.error("Exam Year and Scheme are required");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `/api/mqp/${editingMqp._id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("MQP updated");
      setShowModal(false);
      setEditingMqp(null);
      fetchMQPs();
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update");
    }
  };

  const handleUpload = async () => {
    if (!uploadFormData.scheme) {
      toast.error("Scheme is required");
      return;
    }

    if (!selectedFile) {
      toast.error("Please select a PDF file");
      return;
    }

    if (selectedFile.type !== "application/pdf") {
      toast.error("Only PDF files are allowed");
      return;
    }

    if (selectedFile.size > 20 * 1024 * 1024) {
      toast.error("File size must be less than 20MB");
      return;
    }

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append("file", selectedFile);
    uploadData.append("title", uploadFormData.title || "");
    uploadData.append("description", uploadFormData.description || "");
    uploadData.append("subject", uploadFormData.subject || "");
    uploadData.append(
      "subjectCode",
      (uploadFormData.subjectCode || "").toUpperCase(),
    );
    uploadData.append("branch", "COMMON");
    uploadData.append("semester", "1");
    uploadData.append("examYear", uploadFormData.examYear);
    uploadData.append("examType", uploadFormData.examType);
    uploadData.append("scheme", uploadFormData.scheme);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        "/api/mqp",
        uploadData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        toast.success("MQP uploaded!");
        setShowUploadModal(false);
        setSelectedFile(null);
        setUploadFormData({
          title: "",
          description: "",
          subject: "",
          subjectCode: "",
          examYear: "",
          examType: "Model",
          scheme: "",
        });
        fetchMQPs();
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const allSchemes = [...new Set(mqps.map((m) => m.scheme).filter(Boolean))];

  const filteredMQPs = mqps.filter((mqp) => {
    if (filter !== "all" && mqp.status !== filter) return false;
    if (schemeFilter !== "all" && mqp.scheme !== schemeFilter) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        (mqp.title && mqp.title.toLowerCase().includes(search)) ||
        (mqp.subjectCode && mqp.subjectCode.toLowerCase().includes(search)) ||
        (mqp.subject && mqp.subject.toLowerCase().includes(search)) ||
        (mqp.examYear && mqp.examYear.includes(search))
      );
    }
    return true;
  });

  const getBgColor = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  if (loading)
    return (
      <div style={{ textAlign: "center", padding: "3rem" }}>
        <FaSpinner
          style={{
            animation: "spin 1s linear infinite",
            fontSize: "2rem",
            color: "#ff6a00",
          }}
        />
      </div>
    );

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        <div>
          <h2
            style={{
              fontSize: "1.3rem",
              fontWeight: "bold",
              color: getTextColor(),
            }}
          >
            MQP Management
          </h2>
          <p style={{ color: getTextSecondary(), fontSize: "0.8rem" }}>
            Upload, edit, and manage model question papers
          </p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          style={{
            padding: "0.75rem 1.5rem",
            background: "#ff6a00",
            color: "white",
            border: "none",
            borderRadius: "12px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            fontWeight: "600",
          }}
        >
          <FaPlus /> Upload MQP
        </button>
      </div>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: "1rem",
          marginBottom: "1.5rem",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            background: getBgColor(),
            padding: "0.3rem 0.8rem",
            borderRadius: "40px",
            border: `1px solid ${getBorderColor()}`,
            flex: 1,
            minWidth: "200px",
          }}
        >
          <FaSearch color={getTextSecondary()} />
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              border: "none",
              background: "none",
              outline: "none",
              flex: 1,
              color: getTextColor(),
            }}
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{
            padding: "0.5rem 1rem",
            border: `1px solid ${getBorderColor()}`,
            borderRadius: "40px",
            background: getBgColor(),
            color: getTextColor(),
          }}
        >
          <option value="all">All Status</option>
          <option value="approved">Approved</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
        </select>
        <select
          value={schemeFilter}
          onChange={(e) => setSchemeFilter(e.target.value)}
          style={{
            padding: "0.5rem 1rem",
            border: `1px solid ${getBorderColor()}`,
            borderRadius: "40px",
            background: getBgColor(),
            color: getTextColor(),
          }}
        >
          <option value="all">All Schemes</option>
          {allSchemes.map((s) => (
            <option key={s} value={s}>
              {s} Scheme
            </option>
          ))}
        </select>
        <button
          onClick={() => {
            setSearchTerm("");
            setFilter("all");
            setSchemeFilter("all");
          }}
          style={{
            padding: "0.5rem 1rem",
            background: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "40px",
            cursor: "pointer",
          }}
        >
          Clear
        </button>
      </div>

      {/* Stats */}
      <div
        style={{
          display: "flex",
          gap: "1rem",
          marginBottom: "1.5rem",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            background: getBgColor(),
            padding: "0.3rem 1rem",
            borderRadius: "40px",
          }}
        >
          <span>Total: {mqps.length}</span>
        </div>
        <div
          style={{
            background: getBgColor(),
            padding: "0.3rem 1rem",
            borderRadius: "40px",
          }}
        >
          <span style={{ color: "#10b981" }}>
            Approved: {mqps.filter((p) => p.status === "approved").length}
          </span>
        </div>
        <div
          style={{
            background: getBgColor(),
            padding: "0.3rem 1rem",
            borderRadius: "40px",
          }}
        >
          <span style={{ color: "#f59e0b" }}>
            Pending: {mqps.filter((p) => p.status === "pending").length}
          </span>
        </div>
        <div
          style={{
            background: getBgColor(),
            padding: "0.3rem 1rem",
            borderRadius: "40px",
          }}
        >
          <span style={{ color: "#ef4444" }}>
            Rejected: {mqps.filter((p) => p.status === "rejected").length}
          </span>
        </div>
      </div>

      {/* MQPs List */}
      {filteredMQPs.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "3rem",
            background: getBgColor(),
            borderRadius: "16px",
          }}
        >
          <FaFilePdf size={48} color={getTextSecondary()} />
          <p style={{ color: getTextSecondary(), marginTop: "1rem" }}>
            No MQPs found
          </p>
        </div>
      ) : (
        <div
          style={{ display: "flex", flexDirection: "column", gap: "0.8rem" }}
        >
          {filteredMQPs.map((mqp) => (
            <motion.div
              key={mqp._id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{
                background: getBgColor(),
                borderRadius: "12px",
                padding: "0.8rem",
                border: `1px solid ${getBorderColor()}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "0.8rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.8rem",
                  flex: 1,
                }}
              >
                <FaFilePdf size={28} color="#ef4444" />
                <div>
                  <h4
                    style={{
                      color: getTextColor(),
                      margin: 0,
                      fontSize: "0.9rem",
                    }}
                  >
                    {mqp.title || "Untitled"}
                  </h4>
                  <div
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      flexWrap: "wrap",
                      marginTop: "0.2rem",
                    }}
                  >
                    {mqp.subjectCode && (
                      <span
                        style={{
                          fontSize: "0.65rem",
                          color: "#ff6a00",
                          fontFamily: "monospace",
                        }}
                      >
                        {mqp.subjectCode}
                      </span>
                    )}
                    <span
                      style={{ fontSize: "0.65rem", color: getTextSecondary() }}
                    >
                      <FaCalendarAlt
                        size={10}
                        style={{ display: "inline", marginRight: "0.2rem" }}
                      />{" "}
                      {mqp.examYear}
                    </span>
                    <span
                      style={{ fontSize: "0.65rem", color: getTextSecondary() }}
                    >
                      {mqp.examType}
                    </span>
                    <span style={{ fontSize: "0.65rem", color: "#ff6a00" }}>
                      {mqp.scheme} Scheme
                    </span>
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "0.4rem",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    padding: "0.2rem 0.5rem",
                    borderRadius: "16px",
                    fontSize: "0.6rem",
                    background:
                      mqp.status === "approved"
                        ? "#10b981"
                        : mqp.status === "pending"
                          ? "#f59e0b"
                          : "#ef4444",
                    color: "white",
                  }}
                >
                  {mqp.status}
                </span>
                {mqp.status !== "approved" && (
                  <button
                    onClick={() => updateStatus(mqp._id, "approved")}
                    style={{
                      padding: "0.25rem 0.6rem",
                      background: "#10b981",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "0.65rem",
                    }}
                  >
                    <FaCheck size={10} /> Approve
                  </button>
                )}
                {mqp.status !== "rejected" && (
                  <button
                    onClick={() => updateStatus(mqp._id, "rejected")}
                    style={{
                      padding: "0.25rem 0.6rem",
                      background: "#ef4444",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "0.65rem",
                    }}
                  >
                    <FaTimes size={10} /> Reject
                  </button>
                )}
                <button
                  onClick={() => {
                    setEditingMqp(mqp);
                    setFormData({
                      title: mqp.title || "",
                      description: mqp.description || "",
                      examYear: mqp.examYear,
                      examType: mqp.examType,
                      status: mqp.status,
                      scheme: mqp.scheme,
                    });
                    setShowModal(true);
                  }}
                  style={{
                    padding: "0.25rem 0.6rem",
                    background: "#8b5cf6",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "0.65rem",
                  }}
                >
                  <FaEdit size={10} /> Edit
                </button>
                <button
                  onClick={() =>
                    window.open(buildApiUrl(`/api/mqp/view/${mqp._id}`), "_blank")
                  }
                  style={{
                    padding: "0.25rem 0.6rem",
                    background: "#06b6d4",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "0.65rem",
                  }}
                >
                  <FaEye size={10} /> View
                </button>
                <button
                  onClick={() => deleteMQP(mqp._id)}
                  style={{
                    padding: "0.25rem 0.6rem",
                    background: "#ef4444",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "0.65rem",
                  }}
                >
                  <FaTrash size={10} /> Delete
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {showModal && editingMqp && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: getBgColor(),
              borderRadius: "20px",
              maxWidth: "500px",
              width: "90%",
              padding: "1.5rem",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ color: getTextColor(), marginBottom: "1rem" }}>
              Edit MQP
            </h3>
            <input
              type="text"
              placeholder="Title (Optional)"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              style={{
                width: "100%",
                padding: "0.8rem",
                marginBottom: "0.8rem",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                background: getInputBackground(),
                color: getTextColor(),
              }}
            />
            <input
              type="text"
              placeholder="Subject (Optional)"
              value={formData.subject}
              onChange={(e) =>
                setFormData({ ...formData, subject: e.target.value })
              }
              style={{
                width: "100%",
                padding: "0.8rem",
                marginBottom: "0.8rem",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                background: getInputBackground(),
                color: getTextColor(),
              }}
            />
            <input
              type="text"
              placeholder="Subject Code (Optional)"
              value={formData.subjectCode}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  subjectCode: e.target.value.toUpperCase(),
                })
              }
              style={{
                width: "100%",
                padding: "0.8rem",
                marginBottom: "0.8rem",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                background: getInputBackground(),
                color: getTextColor(),
              }}
            />
            <input
              type="text"
              placeholder="Exam Year *"
              value={formData.examYear}
              onChange={(e) =>
                setFormData({ ...formData, examYear: e.target.value })
              }
              style={{
                width: "100%",
                padding: "0.8rem",
                marginBottom: "0.8rem",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                background: getInputBackground(),
                color: getTextColor(),
              }}
            />
            <select
              value={formData.examType}
              onChange={(e) =>
                setFormData({ ...formData, examType: e.target.value })
              }
              style={{
                width: "100%",
                padding: "0.8rem",
                marginBottom: "0.8rem",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                background: getInputBackground(),
                color: getTextColor(),
              }}
            >
              {examTypes.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Scheme *"
              value={formData.scheme}
              onChange={(e) =>
                setFormData({ ...formData, scheme: e.target.value })
              }
              style={{
                width: "100%",
                padding: "0.8rem",
                marginBottom: "0.8rem",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                background: getInputBackground(),
                color: getTextColor(),
              }}
            />
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value })
              }
              style={{
                width: "100%",
                padding: "0.8rem",
                marginBottom: "1rem",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                background: getInputBackground(),
                color: getTextColor(),
              }}
            >
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
            <div style={{ display: "flex", gap: "1rem" }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  flex: 1,
                  padding: "0.8rem",
                  background: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={updateMQP}
                style={{
                  flex: 1,
                  padding: "0.8rem",
                  background: "#ff6a00",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            overflow: "auto",
            padding: "1rem",
          }}
          onClick={() => setShowUploadModal(false)}
        >
          <div
            style={{
              background: getBgColor(),
              borderRadius: "20px",
              maxWidth: "500px",
              width: "100%",
              padding: "1.5rem",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ color: getTextColor(), marginBottom: "1rem" }}>
              Upload MQP
            </h3>
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${getBorderColor()}`,
                borderRadius: "12px",
                padding: "1.5rem",
                textAlign: "center",
                cursor: "pointer",
                marginBottom: "1rem",
              }}
            >
              <input
                type="file"
                ref={fileInputRef}
                accept=".pdf"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                style={{ display: "none" }}
              />
              {selectedFile ? (
                <div>
                  <FaFilePdf size={32} color="#ef4444" />
                  <p style={{ color: getTextColor(), marginTop: "0.5rem" }}>
                    {selectedFile.name}
                  </p>
                  <p style={{ color: getTextSecondary(), fontSize: "0.7rem" }}>
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <div>
                  <FaUpload size={32} color="#ff6a00" />
                  <p style={{ color: getTextColor(), marginTop: "0.5rem" }}>
                    Click to select PDF
                  </p>
                  <p style={{ color: getTextSecondary(), fontSize: "0.7rem" }}>
                    PDF only, Max 20MB
                  </p>
                </div>
              )}
            </div>
            <input
              type="text"
              placeholder="Title (Optional)"
              value={uploadFormData.title}
              onChange={(e) =>
                setUploadFormData({ ...uploadFormData, title: e.target.value })
              }
              style={{
                width: "100%",
                padding: "0.8rem",
                marginBottom: "0.8rem",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                background: getInputBackground(),
                color: getTextColor(),
              }}
            />
            <input
              type="text"
              placeholder="Subject (Optional)"
              value={uploadFormData.subject}
              onChange={(e) =>
                setUploadFormData({
                  ...uploadFormData,
                  subject: e.target.value,
                })
              }
              style={{
                width: "100%",
                padding: "0.8rem",
                marginBottom: "0.8rem",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                background: getInputBackground(),
                color: getTextColor(),
              }}
            />
            <input
              type="text"
              placeholder="Subject Code (Optional)"
              value={uploadFormData.subjectCode}
              onChange={(e) =>
                setUploadFormData({
                  ...uploadFormData,
                  subjectCode: e.target.value.toUpperCase(),
                })
              }
              style={{
                width: "100%",
                padding: "0.8rem",
                marginBottom: "0.8rem",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                background: getInputBackground(),
                color: getTextColor(),
              }}
            />
            <input
              type="text"
              placeholder="Exam Year (Optional)"
              value={uploadFormData.examYear}
              onChange={(e) =>
                setUploadFormData({
                  ...uploadFormData,
                  examYear: e.target.value,
                })
              }
              style={{
                width: "100%",
                padding: "0.8rem",
                marginBottom: "0.8rem",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                background: getInputBackground(),
                color: getTextColor(),
              }}
            />
            <select
              value={uploadFormData.examType}
              onChange={(e) =>
                setUploadFormData({
                  ...uploadFormData,
                  examType: e.target.value,
                })
              }
              style={{
                width: "100%",
                padding: "0.8rem",
                marginBottom: "0.8rem",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                background: getInputBackground(),
                color: getTextColor(),
              }}
            >
              {examTypes.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Scheme * (e.g., 2022, 2025)"
              value={uploadFormData.scheme}
              onChange={(e) =>
                setUploadFormData({ ...uploadFormData, scheme: e.target.value })
              }
              style={{
                width: "100%",
                padding: "0.8rem",
                marginBottom: "1rem",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                background: getInputBackground(),
                color: getTextColor(),
              }}
            />
            <div style={{ display: "flex", gap: "1rem" }}>
              <button
                onClick={() => setShowUploadModal(false)}
                style={{
                  flex: 1,
                  padding: "0.8rem",
                  background: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading}
                style={{
                  flex: 1,
                  padding: "0.8rem",
                  background: uploading ? "#6c757d" : "#ff6a00",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: uploading ? "not-allowed" : "pointer",
                }}
              >
                {uploading ? (
                  <FaSpinner style={{ animation: "spin 1s linear infinite" }} />
                ) : (
                  <FaUpload />
                )}{" "}
                {uploading ? "Uploading..." : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default MQPManagement;

